import React, { useEffect, useState } from 'react'
import AnswersRadioGroup from '@/src/components/diagnoses/question/answers-radio-group/AnswersRadioGroup'
import { VpButton, VpIcon, VpIconButton, VpSkeleton } from '@vtmn-play/react'
import QuestionImage from '@/src/components/diagnoses/question/question-image/QuestionImage'
import { useNavigate, useOutletContext, useRevalidator } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import TextModal from '@/src/components/text-modal/TextModal'
import useModalPromise from '@/src/hooks/use-modal-promise/useModalPromise'
import ActionChoiceModal from '@/src/components/diagnoses/question/action-choice-modal/ActionChoiceModal'
import packageJson from '../../../../../../../../package.json'
import {
  ActionToAddDto,
  AnswerQuestionRequest,
  DiagnosisQuestionDto,
  FamilyDto,
} from '@/infra/api/generated/model'
import { getDiagnoses } from '@/infra/api/generated/diagnoses/diagnoses'
import { getMedia } from '@/infra/api/generated/media/media'
import { AnswerType } from '@/domains/AnswerType'
import { Answer } from '@/domains/Answer'
import DaoGridHeightModal from '@/src/components/diagnoses/question/dao-grid-height-modal/DaoGridHeightModal'

const { answerQuestion } = getDiagnoses()
const { findMedia } = getMedia()

import { trackEvent } from '@/src/analytics/amplitude'
import { fetchDaoUrlsForQuestion } from '@/infra/questions/questions'
import Modal from '@/src/components/modal/Modal'
import { authPort } from '@/infra/config/beans'
import { useFeatureFlags } from '@/src/context/FeatureFlagContext'
import { FeatureGate } from '@/src/components/feature-flipping/FeatureGate'

const QuestionCard = () => {
  const appVersion = packageJson.version
  const navigate = useNavigate()
  const { question, currentAnswer, family, diagnosisId, handleQuestionAnswer } = useOutletContext<{
    question: DiagnosisQuestionDto
    currentAnswer: AnswerType
    family: FamilyDto
    diagnosisId: string
    handleQuestionAnswer: (questionId: string, answer: Answer) => void
  }>()
  const { t } = useTranslation('common')
  const [answer, setAnswer] = useState<Answer | null>(currentAnswer)
  const [isHelpTextOpen, setIsHelpTextOpen] = useState(false)
  const [isDaoModalOpen, setIsDaoModalOpen] = useState(false)
  const [isDaoGridHeightModalOpen, setIsDaoGridHeightModalOpen] = useState(false)
  const [iframeSource, setIframeSource] = useState('about:blank')
  const queryClient = useQueryClient()
  const { flags, isLoading } = useFeatureFlags()

  const { isVisible, openModalPromise, handleClose } = useModalPromise<ActionToAddDto[] | null>()

  const options = (question.isMandatory ? ['YES', 'NO'] : ['YES', 'NO', 'NC']) as Answer[]
  const [answerIsLoading, setAnswerIsLoading] = useState(false)
  const { revalidate } = useRevalidator()

  const shouldDisplayDaoForQuestion = question.isDaoDisplayed
  const isDAOFeatureEnabled = flags['DAO']

  const {
    data: daoData,
    isError: isDaoError,
    isFetching: isDaoLoading,
  } = useQuery({
    queryKey: ['daoUrls', diagnosisId],
    queryFn: () => fetchDaoUrlsForQuestion(diagnosisId),
    enabled: shouldDisplayDaoForQuestion && isDAOFeatureEnabled,
    retry: false,
  })

  const daoUrls = daoData?.daoUrls ?? []
  const shouldDisplayDaoButton = Boolean(shouldDisplayDaoForQuestion && !isDaoError)

  const handleDaoUrlSelect = async (daoUrl: string) => {
    const user = await authPort.getCurrentUser()
    if (!user?.accessToken) return

    const authenticatedUrl = new URL(daoUrl)
    authenticatedUrl.searchParams.set('Authorization', `Bearer ${user.accessToken}`)
    setIframeSource(authenticatedUrl.toString())
    setIsDaoGridHeightModalOpen(false)
    setIsDaoModalOpen(true)
    trackEvent('DAO_OPEN', { diagnosisId: diagnosisId, questionId: question.questionId })
  }

  const handleAnswerClick = async (newAnswer: Answer) => {
    const isAnswerUpdate = Boolean(answer)

    const previousAnswer = answer

    setAnswerIsLoading(true)

    setAnswer(newAnswer)

    let actionsToAdd: ActionToAddDto[] | undefined = undefined

    if (newAnswer === 'NO') {
      const modalResult = await openModalPromise()

      if (modalResult === null) {
        setAnswerIsLoading(false)
        setAnswer(previousAnswer)
        return
      }

      actionsToAdd = modalResult
    }

    const answerPayload: AnswerQuestionRequest = {
      questionOrder: question.order,
      value: newAnswer,
      actionsToAdd,
    }
    const result = await answerQuestion(diagnosisId, question.questionId, answerPayload)

    if (isAnswerUpdate) trackUpdateAnswer()
    else trackAnswer(newAnswer)
    await queryClient.invalidateQueries({ queryKey: ['diagnoses'] })
    if (actionsToAdd) await revalidate()
    if (result.diagnosisCompleted) {
      await queryClient.invalidateQueries({ queryKey: ['actions'] })
      await queryClient.invalidateQueries({ queryKey: ['priorityDiagnoses'] })
      trackEvent('END_DIAGNOSIS', { diagnosisId: diagnosisId })
      void navigate(`/diagnoses/${diagnosisId}/score`, { viewTransition: true })
    } else {
      handleQuestionAnswer(question.questionId, newAnswer)
    }
    setAnswerIsLoading(false)
  }

  const trackUpdateAnswer = () => {
    trackEvent('UPDATE_ANSWER', { familyId: family.id, questionId: question.questionId })
  }

  const trackAnswer = (newAnswer: AnswerType) => {
    trackEvent('ANSWER_QUESTION', {
      diagnosisId: diagnosisId,
      familyId: family.id,
      questionId: question.questionId,
      answerValue: newAnswer,
    })
  }

  useEffect(() => {
    trackEvent('START_QUESTION', {
      diagnosisId: diagnosisId,
      familyId: family.id,
      questionId: question.questionId,
    })

    return () => {
      trackEvent('END_QUESTION', {
        diagnosisId: diagnosisId,
        familyId: family.id,
        questionId: question.questionId,
      })
    }
  }, [diagnosisId, family.id, question.questionId])

  const { data: imageUrl, isFetching } = useQuery({
    queryKey: ['questionImage', question.imageUrl],
    queryFn: async () => {
      const blob = await findMedia(question.imageUrl!)
      return URL.createObjectURL(blob)
    },
    retry: 0,
    enabled: false,
    initialData: `/imgs/${question.imageUrl}?v=${appVersion}`,
  })

  const handleHelpButtonClick = () => {
    setIsHelpTextOpen(true)
    trackEvent('HELP_BUTTON_CLICK', { questionId: question.questionId })
  }

  const handleDaoButtonClick = () => {
    if (daoUrls.length === 1 && daoUrls[0].daoUrl) {
      void handleDaoUrlSelect(daoUrls[0].daoUrl)
    } else {
      setIsDaoGridHeightModalOpen(true)
    }
    trackEvent('DAO_BUTTON_CLICK', { questionId: question.questionId, diagnosisId: diagnosisId })
  }

  return (
    <>
      <div className="flex flex-col h-full p-4">
        <div className="flex flex-col flex-1 overflow-hidden gap-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center min-w-0">
              <span className="inline-block bg-gray-100 text-gray-800 text-sm font-medium px-3 py-1 mr-2 rounded-full truncate">
                {family.id} - {family.name}
              </span>
            </div>

            <VpButton
              data-testid="question-help-button"
              disabled={!question.helpText}
              variant="tertiary"
              size="small"
              onClick={() => handleHelpButtonClick()}
              className="text-sm text-primary-blue flex items-center gap-1 shrink-0 pr-0"
            >
              {t('pages.questions.help')}
              <VpIcon name="question" />
            </VpButton>

            {question.helpText && (
              <TextModal
                text={question.helpText}
                title={t('pages.questions.help-modal-title')}
                isOpen={isHelpTextOpen}
                handleOpen={() => setIsHelpTextOpen(true)}
                handleClose={() => setIsHelpTextOpen(false)}
              />
            )}
          </div>
          <h1 className="font-bold leading-tight text-lg">{question.label}</h1>
        </div>

        <div className="flex-1 flex flex-col justify-start min-h-0 w-full">
          <div className="w-full flex justify-center shrink min-h-0 overflow-hidden [&>img]:max-h-full [&>img]:object-contain">
            {isFetching ? (
              <VpSkeleton className="w-full h-48 max-w-sm" variant="graphic" shape="rounded" />
            ) : (
              <QuestionImage imageUrl={imageUrl} />
            )}
          </div>

          {shouldDisplayDaoButton && (
            <FeatureGate feature={'DAO'}>
              <div className="shrink-0 flex justify-center w-full mt-3 min-h-8">
                {!isDaoLoading && daoUrls.length > 0 && (
                  <VpButton
                    data-testid="question-dao-link-button"
                    variant="tertiary"
                    size="small"
                    onClick={handleDaoButtonClick}
                    className="text-primary-blue flex items-center gap-1"
                  >
                    <VpIcon name="eye" />
                    {t('pages.questions.open-dao', 'Open the DAO')}
                  </VpButton>
                )}
              </div>
            </FeatureGate>
          )}
        </div>

        <div className="flex flex-col gap-4 mt-4 w-full">
          {options.map((option) => (
            <div key={option} className="flex flex-col items-center w-full">
              <AnswersRadioGroup
                checked={answer === option}
                selectedAnswer={option as Answer}
                setAnswer={handleAnswerClick}
                disabled={answerIsLoading}
              />
              {option === 'NO' && question.actions.length > 0 && (
                <div className="w-full text-xs bg-container-quiet flex justify-between items-center py-1 px-4">
                  <p>
                    {t('pages.questions.count-custom-actions', { count: question.actions.length })}
                  </p>
                  <VpIconButton
                    onClick={() => handleAnswerClick('NO')}
                    variant="ghost"
                    size="small"
                    aria-label="Edit actions"
                  >
                    <VpIcon className="text-primary-blue" size={20} name="edit" />
                  </VpIconButton>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <ActionChoiceModal
        questionId={question.questionId}
        diagnosisId={diagnosisId}
        isOpen={isVisible}
        selectedActions={question.actions}
        handleCloseWithActions={handleClose}
        handleCancel={() => handleClose(null)}
      />

      <Modal
        title={t('pages.questions.dao-modal-title', 'Mon DAO')}
        isOpen={isDaoModalOpen}
        handleOpen={() => setIsDaoModalOpen(true)}
        handleClose={() => setIsDaoModalOpen(false)}
        fullScreen={true}
      >
        <iframe
          src={iframeSource}
          title="DAO Report"
          allow="clipboard-write"
          className="w-full h-full border-0 block bg-white"
        />
      </Modal>
      {isLoading && <span>Loading...</span>}
      {!isLoading && (
        <DaoGridHeightModal
          daoUrls={daoUrls}
          isOpen={isDaoGridHeightModalOpen}
          handleSelect={handleDaoUrlSelect}
          handleClose={() => setIsDaoGridHeightModalOpen(false)}
        />
      )}
    </>
  )
}

export default QuestionCard
